import { useCallback, useEffect, useMemo } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { AddOnType, projectHookPreselect } from "app/contexts/AddonStateContext/const";
import { useAddonsState } from "app/contexts/AddonStateContext/AddonStateContext";
import { useNoTouchPurchaseContext } from "app/contexts/NoTouchPurchaseContext/NoTouchPurchaseContext";
import { useMount } from "app/custom-hooks/useMount";
import { useUnmount } from "app/custom-hooks/useUnmount";
import { HOME_HOOK_NAME } from "app/pages/activation/components/ProjectsList/constants";
import { useHomeContext } from "app/pages/activation/context/Home/HomeContext";
import { ActivationComponentAssets } from "app/pages/activation/helpers/ActivationAssets";
import { useToggle } from "app/pages/activation/hooks/useToggle";
import {
    ACTIVATION_METADATA,
    useActivationMetadataQuery,
} from "app/pages/activation/queries/data/useActivationMetadataQuery";
import { activationTracking } from "app/pages/activation/services/tracking";
import type { CreatePlacement } from "app/pages/activation/services/tracking/home/homepageWithData";
import type { ActivationMetadataItem } from "app/pages/activation/types/api";
import { openHooksModalV5 } from "app/services/ModalService";

export const useProjects = (onCreateProject: () => void) => {
    const {
        totalNumber,
        isActivationLimitReached,
        onSelectActivation,
        activeActivationId,
        refetchActivations,
        images,
        isImagesLoading,
    } = useHomeContext();
    const track = activationTracking.homePageWithData;
    const { data: activationMetadata, isLoading: isMetadataLoading } = useActivationMetadataQuery();

    const activations = useMemo(() => activationMetadata?.activations ?? [], [activationMetadata?.activations]);

    const { getIsAddonAvailable } = useAddonsState();
    const { handlePurchaseAddonClick } = useNoTouchPurchaseContext();

    const [isProjectsListModalOpen, onOpenProjectsListModal, onCloseProjectsListModal] = useToggle();
    const queryClient = useQueryClient();

    const maxProjects = ActivationComponentAssets.activationSetsLimitation;

    const canBuyProjects = useMemo(
        () => getIsAddonAvailable(AddOnType.AdditionalProjects) && maxProjects === 1,
        [getIsAddonAvailable, maxProjects],
    );

    useMount(() => void queryClient.invalidateQueries({ queryKey: [ACTIVATION_METADATA] }));

    useEffect(() => {
        const isActivationIdFromVisible = activations?.some(({ id }) => id === activeActivationId);

        if (!activations.length) {
            onSelectActivation(undefined);
        }

        if (activeActivationId && isActivationIdFromVisible) {
            return;
        }

        onSelectActivation(activations[0]);
    }, [activations, activeActivationId, onSelectActivation]);

    useUnmount(() => {
        onSelectActivation(undefined);
    });

    const onCreateProjectClick = useCallback(
        (place: CreatePlacement) => () => {
            track.trackClickCreateProject(place);

            return isActivationLimitReached ? openHooksModalV5(HOME_HOOK_NAME) : onCreateProject();
        },
        [isActivationLimitReached, onCreateProject, track],
    );

    const onAddonOpen = useCallback(() => {
        track.trackClickCreateProject("plus icon");

        handlePurchaseAddonClick(projectHookPreselect);
    }, [handlePurchaseAddonClick, track]);

    const onActivationItemClick = useCallback(
        (selectedActivation: ActivationMetadataItem) => () => {
            if (activeActivationId === selectedActivation.id) {
                return;
            }

            track.trackSwitchProject();
            onSelectActivation(selectedActivation);
        },
        [activeActivationId, onSelectActivation, track],
    );

    const onViewAllClick = () => {
        refetchActivations();
        onOpenProjectsListModal();
        track.trackViewAllProjects();
    };

    return {
        images,
        isImagesLoading,
        totalNumber,
        activations,
        isMetadataLoading,
        isProjectsListModalOpen,
        isActivationLimitReached,
        onCloseProjectsListModal,
        maxProjects,
        canBuyProjects,
        onCreateProjectClick,
        onAddonOpen,
        onActivationItemClick,
        onViewAllClick,
    };
};
